import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Field, TextInput } from '@salonomy/ui';
import { useAcceptInvite } from '../data/auth-recovery.js';
import { ApiClientError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

/**
 * Staff invite acceptance (pre-session). Reads the raw invite token from `?token=` in the URL,
 * collects a display name + password, and POSTs /v1/auth/invite/accept.
 *
 * Two outcomes (exactly like /login):
 *  • A NEW or non-2FA account → the API sets the session cookie and returns the principal; we re-read
 *    it via auth's `refresh()` and land at the app root.
 *  • An EXISTING account that is already TOTP-enrolled (e.g. an owner of another salon, invited here) →
 *    the API returns a `twoFactorRequired` step-up challenge and NO session. We then show the 2FA code
 *    form and complete it with `verifyTwoFactor`, so accepting an invite can never bypass the enrolled
 *    account's second factor.
 *
 * A missing/invalid/expired token is surfaced as a clear error: an absent `?token=` is caught before
 * any request (so we never POST an empty token), and the API's 400 for an invalid/expired/used invite
 * flows through ApiClientError into the same inline error slot as the Login form.
 */
export function AcceptInvitePage() {
  const { refresh, verifyTwoFactor } = useAuth();
  const accept = useAcceptInvite();
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  // 2FA step-up state: set once invite/accept returns a challenge (the linked account is TOTP-enrolled).
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [verifying, setVerifying] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('This invite link is missing its token. Ask your salon to resend the invite.');
      return;
    }
    try {
      const outcome = await accept.mutateAsync({
        token,
        password,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      });
      // The linked account is 2FA-enrolled: complete the step-up before any session is issued.
      if ('twoFactorRequired' in outcome) {
        setChallengeId(outcome.challengeId);
        setCode('');
        return;
      }
      // The session cookie is now set; re-resolve the principal, then land in the app.
      await refresh();
      window.location.assign(import.meta.env.BASE_URL);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not accept the invite. Check your connection.',
      );
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    if (!challengeId) return;
    setError(null);
    setVerifying(true);
    try {
      const trimmed = code.trim();
      await verifyTwoFactor(
        challengeId,
        useRecovery ? { recoveryCode: trimmed } : { code: trimmed },
      );
      // The step-up issued the session; land in the app.
      window.location.assign(import.meta.env.BASE_URL);
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : 'Could not verify the code. Try again.',
      );
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__mark">
          Salonomy<span style={{ color: 'var(--slm-cobalt)' }}>.</span>
        </div>
        {challengeId ? (
          <>
            <div className="login__sub">
              Two-factor authentication. Enter the {useRecovery ? 'recovery code' : '6-digit code'}{' '}
              from your authenticator app to finish joining.
            </div>
            <form className="login__form" onSubmit={onVerify}>
              {error ? <div className="login__error">{error}</div> : null}
              <Field
                label={useRecovery ? 'Recovery code' : 'Authentication code'}
                htmlFor="totp-code"
              >
                <TextInput
                  id="totp-code"
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={useRecovery ? 'XXXXX-XXXXX' : '123456'}
                />
              </Field>
              <Button type="submit" size="lg" block disabled={verifying}>
                {verifying ? 'Verifying…' : 'Verify'}
              </Button>
              <button
                type="button"
                className="login__linkbtn"
                onClick={() => {
                  setUseRecovery((v) => !v);
                  setCode('');
                  setError(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--slm-cobalt)',
                }}
              >
                {useRecovery ? 'Use an authenticator code instead' : 'Use a recovery code instead'}
              </button>
            </form>
          </>
        ) : (
          <>
            <div className="login__sub">Accept your invite and set a password.</div>
            {token ? (
              <form className="login__form" onSubmit={onSubmit}>
                {error ? <div className="login__error">{error}</div> : null}
                <Field label="Your name" htmlFor="displayName">
                  <TextInput
                    id="displayName"
                    type="text"
                    autoComplete="name"
                    autoFocus
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Alex Stylist"
                  />
                </Field>
                <Field label="Password" htmlFor="password">
                  <TextInput
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={10}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 10 characters"
                  />
                </Field>
                <Button type="submit" size="lg" block disabled={accept.isPending}>
                  {accept.isPending ? 'Setting up…' : 'Accept invite'}
                </Button>
              </form>
            ) : (
              <div className="login__form">
                <div className="login__error">
                  This invite link is missing its token. Ask your salon to resend the invite.
                </div>
                <a
                  href={import.meta.env.BASE_URL}
                  style={{ fontSize: 13, fontWeight: 600, color: 'var(--slm-cobalt)' }}
                >
                  Go to sign in →
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
